import ru from "date-fns/locale/ru";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Controller, useForm } from "react-hook-form";
import { maintenanceType } from "../../../../enums";
import { useFetch } from "../../../../hooks/useFetch";
import { Maintenance } from "../../../../types/types";
import { Button } from "../../../../ui/Button";
import { DatePicker } from "../../../../ui/Datepicker/DatePicker";
import { Input } from "../../../../ui/Input/Input";
import { Label } from "../../../../ui/Label/Label";
import { ScrollArea } from "../../../../ui/ScrollArea/ScrollArea";
import { SelectButton } from "../../../../ui/SelectButton/SelectButton";
import { Textarea } from "../../../../ui/TextArea/Textarea";
import DeleteMaintenance from "./DeleteMaintenance";
registerLocale("es", ru);

type Props = {
  maintenance: Maintenance;
  updateList: Function;
  closeEdit: Function;
};

const EditMaintenance = ({ maintenance, updateList, closeEdit }: Props) => {
  const maintenanceTypes = maintenanceType.map((type) => ({
    label: t(`maintenance_${type}`),
    value: type,
  }));
  const [errors, setErrors] = useState([]);

  let [isDeleteOpen, setIsDeleteOpen] = useState(false);

  function openDeleteModal() {
    setIsDeleteOpen(true);
  }

  function closeDeleteModal() {
    setIsDeleteOpen(false);
  }

  const { handleSubmit, control } = useForm({
    defaultValues: {
      maintenance_name: maintenance?.attributes.description,
      maintenance_type: {
        value: maintenance?.attributes.maintenance_type,
        label: t("maintenance_" + maintenance?.attributes.maintenance_type),
      },
      maintenance_time: maintenance?.attributes.maintenance_time,
      maintenance_previous_date: maintenance?.attributes.previous_maintenance_at
        ? new Date(maintenance?.attributes.previous_maintenance_at)
        : new Date(),
      maintenance_description: maintenance?.attributes.maintenance_description,
    },
  });

  const [
    {
      response: updateResponse,
      isLoading: updateIsLoading,
      error: updateError,
    },
    doFetchItemUpdate,
  ] = useFetch(
    `/directories/equipment_items/${maintenance.attributes.equipment_item_id}/maintenances/${maintenance.id}`,
  );

  const onUpdate = (formData: any) => {
    doFetchItemUpdate({
      method: "PUT",
      data: {
        data: {
          previous_maintenance_at:
            formData.maintenance_previous_date || new Date(),
          description: formData.maintenance_name || null,
          maintenance_time: formData.maintenance_time || null,
          maintenance_type: formData.maintenance_type.value,
          maintenance_description: formData.maintenance_description || "",
        },
      },
    });
  };

  useEffect(() => {
    if (updateResponse) {
      updateList();
    }
  }, [updateResponse]);

  return (
    <>
      <ScrollArea className="w-full">
        <div className="flex flex-col w-full">
          <form onSubmit={handleSubmit(onUpdate)}>
            <div className="flex flex-col w-full pt-8 h-full gap-3">
              <Controller
                name="maintenance_name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Input {...field} label={`${t("maintenance_name")}*`} />
                  </div>
                )}
              />
              <Controller
                name="maintenance_type"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("maintenance_type")}*</Label>
                    <SelectButton
                      label={"choose_maintenance_type"}
                      searchable={false}
                      options={maintenanceTypes}
                      value={
                        value
                          ? maintenanceTypes?.find(
                              (s) => s.value == String(value.value),
                            )!
                          : null
                      }
                      onChange={(e) => onChange(e?.value)}
                    />
                  </div>
                )}
              />
              <Controller
                name="maintenance_time"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Input {...field} label={`${t("maintenance_time")}*`} />
                  </div>
                )}
              />
              <Controller
                name="maintenance_previous_date"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("maintenance_previous_date")}</Label>
                    <DatePicker
                      mode="single"
                      algin="start"
                      selected={value!}
                      onSelect={onChange}
                    />
                  </div>
                )}
              />
              <Controller
                name="maintenance_description"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Label>{t("maintenance_description")}*</Label>
                    <Textarea {...field} />
                  </div>
                )}
              />
              <div className="flex justify-between items-center">
                <Button
                  variant={"destructive"}
                  type="button"
                  onClick={openDeleteModal}
                >
                  {t("delete")}
                </Button>
                <Button type="submit">{t("save")}</Button>
              </div>
            </div>
          </form>
        </div>
      </ScrollArea>
      <DeleteMaintenance
        maintenanceId={maintenance.id}
        equipmentId={String(maintenance.attributes.equipment_item_id)}
        isDeleteOpen={isDeleteOpen}
        closeModal={closeDeleteModal}
        closeEdit={closeEdit}
        updateList={updateList}
      />
    </>
  );
};

export default EditMaintenance;
